import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';

// Minimal STL parser
function parseSTL(buffer) {
  const isASCII = (buf) => {
    const str = new TextDecoder().decode(new Uint8Array(buf, 0, 80));
    return str.startsWith('solid') && !str.includes('\0');
  };

  if (isASCII(buffer)) {
    return parseSTLASCII(new TextDecoder().decode(buffer));
  }
  return parseSTLBinary(buffer);
}

function parseSTLBinary(buffer) {
  const view = new DataView(buffer);
  const triangleCount = view.getUint32(80, true);
  const positions = new Float32Array(triangleCount * 9);
  const normals = new Float32Array(triangleCount * 9);

  for (let i = 0; i < triangleCount; i++) {
    const offset = 84 + i * 50;
    const nx = view.getFloat32(offset, true);
    const ny = view.getFloat32(offset + 4, true);
    const nz = view.getFloat32(offset + 8, true);

    for (let v = 0; v < 3; v++) {
      const vOffset = offset + 12 + v * 12;
      positions[i * 9 + v * 3] = view.getFloat32(vOffset, true);
      positions[i * 9 + v * 3 + 1] = view.getFloat32(vOffset + 4, true);
      positions[i * 9 + v * 3 + 2] = view.getFloat32(vOffset + 8, true);
      normals[i * 9 + v * 3] = nx;
      normals[i * 9 + v * 3 + 1] = ny;
      normals[i * 9 + v * 3 + 2] = nz;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  return geometry;
}

function parseSTLASCII(text) {
  const positions = [];
  const normals = [];
  const normalRegex = /facet\s+normal\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)/g;
  const vertexRegex = /vertex\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)/g;

  let nm;
  while ((nm = normalRegex.exec(text)) !== null) {
    const nx = parseFloat(nm[1]), ny = parseFloat(nm[2]), nz = parseFloat(nm[3]);
    for (let i = 0; i < 3; i++) { normals.push(nx, ny, nz); }
  }
  let vm;
  while ((vm = vertexRegex.exec(text)) !== null) {
    positions.push(parseFloat(vm[1]), parseFloat(vm[2]), parseFloat(vm[3]));
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return geometry;
}

export default function STLViewer({ url, width = '100%', height = 400 }) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);

  const init = useCallback(async () => {
    if (!containerRef.current || !url) return;

    // Cleanup previous
    if (rendererRef.current) {
      rendererRef.current.dispose();
      cancelAnimationFrame(frameRef.current);
    }
    containerRef.current.innerHTML = '';

    const container = containerRef.current;
    const w = container.clientWidth;
    const h = container.clientHeight || 400;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1e1e28);

    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 10000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    scene.add(new THREE.AmbientLight(0x404050, 2));
    const dirLight = new THREE.DirectionalLight(0xffffff, 2);
    dirLight.position.set(5, 10, 7);
    scene.add(dirLight);
    const dirLight2 = new THREE.DirectionalLight(0x8888ff, 1);
    dirLight2.position.set(-5, -5, -5);
    scene.add(dirLight2);

    // Grid
    const grid = new THREE.GridHelper(200, 20, 0x3a3a4e, 0x2e2e3e);
    scene.add(grid);

    try {
      const response = await fetch(url, { credentials: 'include' });
      const buffer = await response.arrayBuffer();
      const geometry = parseSTL(buffer);
      geometry.computeBoundingBox();
      geometry.center();

      const material = new THREE.MeshPhongMaterial({
        color: 0x6366f1,
        specular: 0x333366,
        shininess: 40,
        flatShading: false,
      });

      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // Fit camera
      const box = geometry.boundingBox;
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      const dist = maxDim / (2 * Math.tan(fov / 2)) * 1.8;
      camera.position.set(dist * 0.7, dist * 0.5, dist * 0.7);
      camera.lookAt(0, 0, 0);

      // Simple orbit
      let isDragging = false;
      let prevX = 0, prevY = 0;
      let theta = Math.atan2(camera.position.x, camera.position.z);
      let phi = Math.acos(camera.position.y / dist);
      let radius = dist;

      const onDown = (e) => { isDragging = true; prevX = e.clientX; prevY = e.clientY; };
      const onUp = () => { isDragging = false; };
      const onMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - prevX;
        const dy = e.clientY - prevY;
        theta -= dx * 0.01;
        phi = Math.max(0.1, Math.min(Math.PI - 0.1, phi + dy * 0.01));
        camera.position.x = radius * Math.sin(phi) * Math.sin(theta);
        camera.position.y = radius * Math.cos(phi);
        camera.position.z = radius * Math.sin(phi) * Math.cos(theta);
        camera.lookAt(0, 0, 0);
        prevX = e.clientX;
        prevY = e.clientY;
      };
      const onWheel = (e) => {
        radius = Math.max(maxDim * 0.5, Math.min(maxDim * 5, radius + e.deltaY * 0.1));
        camera.position.x = radius * Math.sin(phi) * Math.sin(theta);
        camera.position.y = radius * Math.cos(phi);
        camera.position.z = radius * Math.sin(phi) * Math.cos(theta);
        camera.lookAt(0, 0, 0);
      };

      renderer.domElement.addEventListener('mousedown', onDown);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('mousemove', onMove);
      renderer.domElement.addEventListener('wheel', onWheel);
    } catch (err) {
      console.error('Failed to load STL:', err);
    }

    // Animate
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const onResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight || 400;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);
  }, [url]);

  useEffect(() => {
    init();
    return () => {
      if (rendererRef.current) rendererRef.current.dispose();
      cancelAnimationFrame(frameRef.current);
    };
  }, [init]);

  return <div ref={containerRef} style={{ width, height, borderRadius: 'var(--radius)', overflow: 'hidden' }} />;
}
